import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Filter, ArrowRight, X, ChevronLeft, ChevronRight } from "lucide-react";

const CATEGORIES = [
  { key: "all", label: "Tümü" },
  { key: "konut", label: "Konut (Toplu İş)" },
  { key: "ev", label: "Ev" },
  { key: "isyeri", label: "İşyeri" },
];

export default function WorksPage() {
  const [projects, setProjects] = useState([]);
  const [types, setTypes] = useState([]);
  const [cat, setCat] = useState("all");
  const [ftype, setFtype] = useState("all");
  const [selected, setSelected] = useState(null);
  const [activeImgIdx, setActiveImgIdx] = useState(0);

  useEffect(() => {
    (async () => {
      const [p, t] = await Promise.all([
        api.get("/projects", { params: { active_only: true } }).then((r) => r.data),
        api.get("/furniture-types", { params: { active_only: true } }).then((r) => r.data),
      ]);
      setProjects(p);
      setTypes(t);
    })();
  }, []);

  const filtered = useMemo(() => {
    return projects.filter((p) => {
      if (cat !== "all" && p.category !== cat) return false;
      if (ftype !== "all" && p.furniture_type !== ftype) return false;
      return true;
    });
  }, [projects, cat, ftype]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12" data-testid="works-page">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-600 mb-3">Çalışmalarımız</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-ink-900">
          Tamamladığımız <span className="gradient-text">Projeler</span>
        </h1>
        <p className="mt-3 text-ink-500 max-w-2xl">Toplu iş projelerinden bireysel ev ve iş yeri çalışmalarına kadar, atölyemizden çıkan seçkin işler.</p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-ink-500"><Filter className="h-4 w-4" /> Kategori</div>
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`filter-cat-${c.key}`}
            onClick={() => setCat(c.key)}
            className={`rounded-full border-2 px-4 py-1.5 text-xs font-semibold transition-all ${cat === c.key ? "border-brand-500 bg-brand-50 text-brand-700" : "border-ink-100 text-ink-700 hover:border-brand-300"}`}
          >{c.label}</button>
        ))}
      </div>

      <div className="mb-10 flex flex-wrap items-center gap-2">
        <div className="text-xs font-bold uppercase tracking-widest text-ink-500 mr-2">Mobilya Türü:</div>
        <button
          data-testid="filter-type-all"
          onClick={() => setFtype("all")}
          className={`rounded-full border-2 px-3 py-1 text-xs font-semibold ${ftype === "all" ? "border-brand-500 bg-brand-50 text-brand-700" : "border-ink-100 text-ink-700"}`}
        >Tümü</button>
        {types.map((t) => (
          <button
            key={t.id}
            data-testid={`filter-type-${t.slug}`}
            onClick={() => setFtype(t.name)}
            className={`rounded-full border-2 px-3 py-1 text-xs font-semibold ${ftype === t.name ? "border-brand-500 bg-brand-50 text-brand-700" : "border-ink-100 text-ink-700"}`}
          >{t.name}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-ink-100 py-24 text-center">
          <Sparkles className="h-8 w-8 mx-auto text-ink-500" />
          <p className="mt-3 text-ink-500">Bu filtre için henüz proje bulunmuyor.</p>
        </div>
      ) : (
        <div className="space-y-14">
          {filtered.map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              onOpen={(imgIdx = 0) => { setActiveImgIdx(imgIdx); setSelected(p); }}
              slug={types.find((t) => t.name === p.furniture_type)?.slug || ""}
            />
          ))}
        </div>
      )}

      {selected && (
        <LightBox
          project={selected}
          activeIdx={activeImgIdx}
          setActiveIdx={setActiveImgIdx}
          onClose={() => setSelected(null)}
          slug={types.find((t) => t.name === selected.furniture_type)?.slug || ""}
        />
      )}
    </div>
  );
}

function ProjectRow({ project, onOpen, slug }) {
  const scrollRef = useRef(null);
  const images = project.images && project.images.length > 0 ? project.images : [];

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: "smooth" });
  };

  return (
    <section className="group" data-testid={`work-row-${project.id}`}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-brand-600">{project.furniture_type} · {project.category}</div>
          <h2 className="mt-1 font-display font-bold text-2xl md:text-3xl text-ink-900">{project.title}</h2>
          {project.description && <p className="mt-2 text-ink-500 max-w-xl">{project.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/fiyat-hesaplama?tur=${slug || encodeURIComponent(project.furniture_type || "")}`}
            data-testid={`work-similar-${project.id}`}
          >
            <Button variant="outline" className="rounded-full text-xs font-bold uppercase border-2 border-ink-900 hover:bg-ink-900 hover:text-white">
              Benzerini Tasarla <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </Link>
          {images.length > 1 && (
            <div className="hidden md:flex items-center gap-1">
              <button data-testid={`scroll-left-${project.id}`} onClick={() => scroll(-1)} className="h-10 w-10 grid place-items-center rounded-full border-2 border-ink-100 hover:border-brand-500 hover:text-brand-600">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button data-testid={`scroll-right-${project.id}`} onClick={() => scroll(1)} className="h-10 w-10 grid place-items-center rounded-full border-2 border-ink-100 hover:border-brand-500 hover:text-brand-600">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 admin-scroll"
        style={{ scrollbarWidth: "thin" }}
      >
        {images.map((src, i) => (
          <button
            key={`${project.id}-img-${src || i}`}
            onClick={() => onOpen(i)}
            data-testid={`work-img-${project.id}-${i}`}
            className={`relative shrink-0 snap-start overflow-hidden rounded-3xl bg-ink-50 group/img
              ${i === 0 ? "w-[86%] sm:w-[72%] lg:w-[58%] aspect-[16/10]" : "w-[65%] sm:w-[46%] lg:w-[34%] aspect-[4/3]"}`}
          >
            <img
              src={src}
              alt={`${project.title} ${i + 1}. açı`}
              className="w-full h-full object-cover transition-transform duration-700 group-hover/img:scale-105"
              loading="lazy"
            />
            <div className="absolute top-3 left-3 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-ink-700">
              {i + 1} / {images.length}
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/img:opacity-100 transition-opacity" />
          </button>
        ))}
      </div>
    </section>
  );
}

function LightBox({ project, activeIdx, setActiveIdx, onClose, slug }) {
  const images = project.images || [];
  const prev = () => setActiveIdx((activeIdx - 1 + images.length) % images.length);
  const next = () => setActiveIdx((activeIdx + 1) % images.length);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm p-4 flex items-center justify-center"
      onClick={onClose}
      data-testid="work-modal"
    >
      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="relative aspect-[16/10] rounded-3xl overflow-hidden bg-ink-900">
          <img src={images[activeIdx]} alt={project.title} className="w-full h-full object-contain" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-11 w-11 grid place-items-center rounded-full bg-white/95 hover:bg-white text-ink-900"
            data-testid="lightbox-close"
          >
            <X className="h-5 w-5" />
          </button>
          {images.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center rounded-full bg-white/95 hover:bg-white text-ink-900"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={next}
                className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center rounded-full bg-white/95 hover:bg-white text-ink-900"
                data-testid="lightbox-next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute bottom-4 left-4 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-xs font-bold text-ink-900">
                {activeIdx + 1} / {images.length}
              </div>
            </>
          )}
        </div>
        {images.length > 1 && (
          <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
            {images.map((src, i) => (
              <button
                key={`thumb-${src || i}`}
                onClick={() => setActiveIdx(i)}
                className={`shrink-0 w-24 h-16 rounded-xl overflow-hidden border-2 transition-all ${activeIdx === i ? "border-brand-500" : "border-transparent opacity-60 hover:opacity-100"}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-white">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-brand-300">{project.furniture_type}</div>
            <h3 className="font-display font-bold text-2xl">{project.title}</h3>
          </div>
          <Link to={`/fiyat-hesaplama?tur=${slug || encodeURIComponent(project.furniture_type || "")}`}>
            <Button className="gradient-primary text-white rounded-full font-bold uppercase text-xs tracking-wider">
              Benzerini Tasarla <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
